/**
 * R6-A — Conferma eliminazione scarico/produzione con testo dinamico (area, kg,
 * segno) e avvertenza Ecomatico. Base ui/Modal (pattern ProformaInviaModal).
 * L'autorità resta il 422 del backend (già-registrato-Ecomatico); qui è UX.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import { fmtKg, parseSaldoInsufficiente } from '../../lib/saldo';

interface Props {
  title: string;
  /** Righe descrittive dell'effetto (già localizzate dal chiamante). */
  righe: string[];
  /** Avvertenza in evidenza (es. Ecomatico). */
  avvertenza: string;
  /**
   * Etichetta del bottone di conferma. Default «Elimina».
   * Esiste per l'annullamento di un'uscita, che NON è un'eliminazione: i big
   * bag tornano disponibili e il FIR torna libero. Chiamarlo «Elimina» direbbe
   * all'operatore che sta distruggendo qualcosa.
   */
  confermaLabel?: string;
  /**
   * Etichetta del campo MOTIVO. Se presente, il motivo diventa obbligatorio:
   * compare una textarea e la conferma resta disabilitata finché è vuota.
   *
   * Serve all'eliminazione di un conferimento stoccato, dove lo storno porta il
   * netto a ZERO e il controllo 2 della diagnostica smette per costruzione di
   * vedere la riga: il motivo è l'unica traccia leggibile che resta. Assente
   * per scarichi, produzioni e uscite, dove il movimento di compensazione
   * racconta già da solo cos'è successo.
   */
  motivoLabel?: string;
  motivoPlaceholder?: string;
  onConfirm: (motivo: string) => Promise<void>;
  onClose: () => void;
  testid?: string;
}

export default function EliminaMagazzinoModal({
  title, righe, avvertenza, confermaLabel, motivoLabel, motivoPlaceholder,
  onConfirm, onClose, testid,
}: Props) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const motivoMancante = motivoLabel !== undefined && motivo.trim() === '';

  async function handle() {
    setBusy(true); setError(null);
    try {
      await onConfirm(motivo.trim());
    } catch (err: unknown) {
      // Eliminare una produzione i cui kg sono già usciti dall'area è rifiutato
      // dalla guardia sul saldo: il detail è un oggetto e senza questo ramo
      // finirebbe nel messaggio generico, perdendo i numeri che spiegano perché.
      const saldo = parseSaldoInsufficiente(err);
      if (saldo) {
        setError(t(`magazzino.saldo.${saldo.code}`, {
          materiale: saldo.materiale, area: saldo.area_codice ?? '',
          disponibile: fmtKg(saldo.disponibile_kg), richiesto: fmtKg(saldo.richiesto_kg),
          mancante: fmtKg(saldo.mancante_kg),
        }));
        setBusy(false);
        return;
      }
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('magazzino.elimina.errore'));
      setBusy(false);   // resta aperto per mostrare l'errore (es. 422 Ecomatico)
    }
  }

  return (
    <Modal
      title={title}
      onClose={onClose}
      testid={testid ?? 'elimina-magazzino-modal'}
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={busy}
            className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">
            {t('common.azioni.annulla')}
          </button>
          <button type="button" onClick={() => void handle()} disabled={busy || motivoMancante} data-testid="btn-conferma-elimina"
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60">
            {busy ? t('magazzino.elimina.in_corso') : (confermaLabel ?? t('magazzino.elimina.conferma'))}
          </button>
        </>
      )}
    >
      <div className="space-y-2 text-sm text-ink-dark">
        {righe.map((r, i) => <p key={i}>{r}</p>)}
      </div>
      {motivoLabel !== undefined && (
        <label className="mt-3 block text-sm text-ink-dark">
          {motivoLabel}
          <textarea rows={2} value={motivo} disabled={busy}
            data-testid="elimina-motivo" placeholder={motivoPlaceholder}
            onChange={(e) => setMotivo(e.target.value)}
            className="mt-1 w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage bg-white disabled:opacity-60" />
        </label>
      )}
      <div className="mt-3 px-3 py-2 bg-accent-warning-bg border border-[#FDE68A] rounded text-sm text-ink-mid" data-testid="elimina-avvertenza">
        {avvertenza}
      </div>
      {error && (
        <div className="mt-3 px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger" data-testid="elimina-error">
          {error}
        </div>
      )}
    </Modal>
  );
}
